import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.js';
import { usePortalLogout } from './useLogout.js';
import { cn } from '../lib/cn.js';
import { visibleNav, type PortalNavItem } from './portalModules.js';
import { useTitle } from '../lib/use-title.js';
import { surface, type } from './theme.js';

/**
 * 工厂端外壳。**同一份导航数据，两种壳**：
 *
 *   手机 / 竖屏平板（< md）  底部标签栏 —— 拇指够得着的是屏幕下缘
 *   横屏平板 / 桌面（≥ md）  左侧栏 —— 屏幕横向有的是地方，纵向才是稀缺的
 *
 * ── 为什么改，以及原来那句注释错在哪 ──────────────────────────────
 *
 * 原来写的是「主路径是手机，桌面是把内容加宽，不是另一套布局」。
 * 前半句对（工厂老板确实是扫码从微信里进来），后半句错：加宽到 `max-w-3xl` 就停了，
 * 底部那条 `fixed` 导航仍然横跨整屏，1440 的屏上七个图标摊在一条 768 宽的横条上，
 * 两边大片空白 —— 长得像一个被放大的手机页。
 *
 * 而桌面不是边角场景：展会大屏上演示、跟单坐在办公室电脑上处理订单、
 * 微信 PC 版打开服务号消息里的链接，都是桌面宽度。
 *
 * ── 断点取 `md` 不取 `lg` ─────────────────────────────────────────
 *
 * iPad 竖屏 768 宽，放一条 224px 的侧栏还剩 544 给内容，够；
 * 微信 PC 版的内置窗口大约 800 到 1000 宽，正好落在侧栏那一档。
 * 取 `lg`（1024）的话这两种最常见的「桌面」反而还在用手机布局。
 *
 * **手机那一半一个像素都没动。** 这一单只加桌面。
 */
export default function PortalLayout() {
  const { user, tenant } = useAuth();
  const isInbox = useLocation().pathname.startsWith('/portal/inquiries');
  const nav = visibleNav(tenant?.tier, tenant?.capabilities);
  // index.html 里那个静态标题是内部后台的（「Sitebox · admin」）—— 同一份产物
  // 服务两个域名，静态改不了，只能进来之后覆盖掉。工厂不该在标签页上看到我们的内部品牌。
  useTitle(tenant?.name ? `${tenant.name} · 工厂端` : '工厂端');

  const { logout, pending } = usePortalLogout();
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  /**
   * 工厂名 / 当前账号 + 退出。
   *
   * 退出按钮放在这里，手机顶栏和桌面侧栏**一次都有** —— 两个壳共用这一段。
   * 分别写两遍的话，加一个东西必漏一边（导航那边就是为了这个抽成 NavItem 的）。
   *
   * 只画一个图标不写字：这一行还要放工厂名和账号名，手机上宽度是稀缺的。
   * `aria-label` 和 `title` 补上语义，账号页底部另有一个写全字的按钮 ——
   * 手机上人找「退出」的第一反应是去「我的」页翻到底。
   */
  const who = (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
      <div className="min-w-0">
        <div className={cn(type.body, 'truncate font-semibold')}>{tenant?.name ?? '工厂端'}</div>
        <div className={type.meta}>
          {user?.name}
          {tenant?.tier === 'free' ? ' · 免费' : ' · Pro'}
        </div>
      </div>
      <button
        onClick={() => void logout()}
        disabled={pending}
        className="shrink-0 rounded-portal-md p-2 text-portal-mute transition-colors hover:bg-portal-tint2 hover:text-portal-ink disabled:opacity-50"
        aria-label="退出登录"
        title="退出登录"
        data-testid="portal-logout"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );

  /**
   * 到期横幅（P1 到期回收）。**在他点任何按钮之前就出现** ——
   * 敲完一段话点发送才被 403 告知「服务到期了」，那段话就白写了
   * （跟回复窗口那条是同一个判据：能不能做，要在动手之前说）。
   */
  const readOnly = tenant?.read_only === true;
  const graceDays = tenant?.grace_ends_at
    ? Math.max(0, Math.ceil((new Date(tenant.grace_ends_at).getTime() - Date.now()) / 86400000))
    : null;

  return (
    <div className={cn('min-h-[100dvh] font-portal', surface.page)} data-testid="portal-layout">
      {readOnly && (
        <div
          className="sticky top-0 z-30 bg-portal-warn px-4 py-2 text-center text-portal-meta font-medium text-white"
          data-testid="portal-expired-banner"
        >
          服务已到期，只能查看和导出
          {graceDays !== null ? `，还剩 ${graceDays} 天可导出` : ''}。续费后恢复。
        </div>
      )}
      {/* ── 桌面侧栏（≥ md）────────────────────────────────────── */}
      {/*
        可折叠成只剩图标的窄条：询盘页是三栏（联系人 / 会话 / 资料），
        1000 来宽的笔记本上 224 的侧栏会把会话栏挤成一列两三个字。
        折叠后工厂名收成首字、菜单名收进 title 提示，退出按钮挪到底部。
      */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-20 hidden flex-col border-r border-portal-line bg-portal-paper transition-[width] duration-150 md:flex',
          collapsed ? 'w-16' : 'w-56',
        )}
        aria-label="工厂端导航"
        data-testid="portal-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        {collapsed ? (
          <div className="flex justify-center border-b border-portal-line py-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-portal-md bg-portal-accent-soft font-semibold text-portal-accent"
              title={tenant?.name ?? '工厂端'}
            >
              {(tenant?.name ?? '工').slice(0, 1)}
            </div>
          </div>
        ) : (
          <div className="flex border-b border-portal-line px-4 py-3">{who}</div>
        )}
        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="space-y-0.5">
            {nav.map((n) => (
              <li key={n.id}>
                <NavItem item={n} variant="side" collapsed={collapsed} />
              </li>
            ))}
          </ul>
        </nav>
        <div
          className={cn(
            'flex border-t border-portal-line p-2',
            collapsed ? 'flex-col items-center gap-1' : 'justify-end',
          )}
        >
          {collapsed && (
            <button
              onClick={() => void logout()}
              disabled={pending}
              className="rounded-portal-md p-2 text-portal-mute transition-colors hover:bg-portal-tint2 hover:text-portal-ink disabled:opacity-50"
              aria-label="退出登录"
              title="退出登录"
              data-testid="portal-logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="rounded-portal-md p-2 text-portal-mute transition-colors hover:bg-portal-tint2 hover:text-portal-ink"
            aria-label={collapsed ? '展开菜单' : '收起菜单'}
            title={collapsed ? '展开菜单' : '收起菜单'}
            data-testid="portal-collapse-toggle"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>
      </aside>

      <div className={cn('flex min-h-screen flex-col', collapsed ? 'md:pl-16' : 'md:pl-56')}>
        {/*
          顶栏只有手机要：桌面上工厂名和用户已经在侧栏顶部了，再来一条就是重复。
          手机那条原样保留（sticky、同样的内容和间距）。
        */}
        <header className="sticky top-0 z-10 border-b border-portal-line bg-portal-paper px-4 py-3 md:hidden">
          <div className="mx-auto flex max-w-3xl items-center justify-between">{who}</div>
        </header>

        {/*
          `pb-24` 是给底部导航留的位置 —— 桌面上没有底部导航，
          再留一截空白会让人觉得「页面到不了底」。所以 md 之后收掉。
        */}
        <main
          className={cn(
            'flex-1 pb-24 md:pb-8',
            /*
              询盘(IM 收件箱)要满宽:左联系人 + 中会话 + 右资料三栏铺满,别被居中的
              max-w-5xl 夹在中间留一片空白。其它页(表单/列表)保持居中窄栏更好读。
            */
            isInbox
              ? 'w-full px-0 md:px-4 md:py-4'
              : 'mx-auto w-full max-w-3xl px-4 py-4 md:max-w-5xl',
          )}
        >
          <Outlet />
        </main>
      </div>

      {/* ── 手机底部导航（< md）—— 这一段跟改之前逐字一样 ──────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-10 border-t border-portal-line bg-portal-paper pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="工厂端导航"
        data-testid="portal-nav"
      >
        <ul className="mx-auto flex max-w-3xl">
          {nav.map((n) => (
            <li key={n.id} className="flex-1">
              <NavItem item={n} variant="bottom" />
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * 一个导航项的两种画法。
 *
 * 抽出来是为了让「两边同源」成为**结构上的事实**而不是靠人记得：
 * 两处都 `nav.map` 同一个数组、都渲染这一个组件，加一项不可能只加一边。
 * 用例里还有一条断言兜底（见 portal.test.tsx）。
 *
 * `data-testid` 两边刻意不同（`portal-nav-x` / `portal-sidebar-x`）——
 * 两个 DOM 节点同时存在（靠 CSS 隐藏其一），testid 一样的话
 * playwright 的 strict mode 会因为「匹配到两个」直接报错。
 */
function NavItem({
  item,
  variant,
  collapsed = false,
}: {
  item: PortalNavItem;
  variant: 'bottom' | 'side';
  /** 侧栏折叠：只画图标，菜单名进 title / aria-label */
  collapsed?: boolean;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/portal'}
      className={({ isActive }) =>
        cn(
          'transition-colors',
          variant === 'bottom'
            ? 'flex flex-col items-center gap-0.5 py-2.5 text-portal-meta'
            : collapsed
              ? 'flex items-center justify-center rounded-portal-md py-2 text-portal-body'
              : 'flex items-center gap-2.5 rounded-portal-md px-3 py-2 text-portal-body',
          isActive
            ? variant === 'side'
              ? 'bg-portal-accent-soft font-medium text-portal-accent'
              : 'font-medium text-portal-accent'
            : cn(
                'text-portal-char',
                variant === 'side' && 'hover:bg-portal-tint hover:text-portal-ink',
              ),
        )
      }
      data-testid={`${variant === 'bottom' ? 'portal-nav' : 'portal-sidebar'}-${item.id}`}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {variant === 'bottom' ? (item.shortLabel ?? item.label) : collapsed ? null : item.label}
    </NavLink>
  );
}

const COLLAPSE_KEY = 'portal.sidebar.collapsed';

/**
 * 侧栏折叠状态。点过就记住（per 浏览器）；没点过时按屏宽给默认：
 * 1280 以下默认折叠 —— 那一档正好是询盘三栏被挤的宽度。
 * 只在首次渲染读一次宽度，不挂 resize 监听：拖窗口不该替人改主意。
 */
function useSidebarCollapsed(): [boolean, (v: boolean) => void] {
  const [collapsed, setState] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch {
      /* 隐私模式读不到就走默认 */
    }
    return typeof window !== 'undefined' && window.innerWidth < 1280;
  });
  const set = (v: boolean): void => {
    setState(v);
    try {
      localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
    } catch {
      /* 存不下就只在本次会话生效 */
    }
  };
  return [collapsed, set];
}
